/**
 * The transition package's header in the Generate package view: generate a
 * Word version, download any version. Generating waits until every section is
 * approved; the view below it shows the document as it stands meanwhile.
 */
import { useEffect, useState } from 'react'
import { Download, FileText, Loader2 } from 'lucide-react'
import { Button } from '../ui'
import { transitApi } from '../../services/transit'
import type { DocumentVersion, TransitionGraph } from '../../types/transit'
import { timeAgo } from './status'

export default function PackageSection({ transitionId, graph }: { transitionId: string; graph: TransitionGraph }) {
  const [versions, setVersions] = useState<DocumentVersion[]>([])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    transitApi.listPackageVersions(transitionId).then(setVersions).catch((e) => setError(e.message))
  }, [transitionId])

  const total = graph.nodes.length
  const approved = graph.nodes.filter((n) => n.status === 'done').length
  const ready = total > 0 && approved === total
  // A version is current when every section is at the revision it was built from.
  const current = versions[0] && graph.nodes.every((n) => (versions[0].node_revisions[n.node_key] ?? 0) === n.revision)

  const generate = async () => {
    setBusy(true); setError(null)
    try {
      const v = await transitApi.generatePackage(transitionId)
      setVersions((list) => [v, ...list])
      await transitApi.downloadPackage(transitionId, v.version)
    } catch (e: any) {
      setError(e.message)
    } finally {
      setBusy(false)
    }
  }
  const download = (version: number) => transitApi.downloadPackage(transitionId, version).catch((e) => setError(e.message))

  return (
    <section data-tour="package" className="rounded-xl border border-border bg-card p-5 space-y-3">
      <div className="flex flex-wrap items-start gap-3">
        <div className="flex-1 min-w-[240px]">
          <h2 className="font-semibold text-foreground">Transition package</h2>
          <p className="text-sm text-muted-foreground">
            One Word document with every section in order, as shown below.{' '}
            {ready
              ? 'Every section is approved.'
              : `${approved} of ${total} sections approved. Approve all ${total} to generate the package.`}
          </p>
        </div>
        <Button onClick={generate} disabled={busy || !ready || !!current}
          title={!ready ? `${total - approved} section${total - approved === 1 ? '' : 's'} still to approve` : current ? 'The latest version already reflects every section' : undefined}>
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
          {current ? 'Up to date' : versions.length ? 'Generate new version' : 'Generate package'}
        </Button>
      </div>
      {error && <div className="text-xs text-destructive">{error}</div>}
      {versions.length > 0 && (
        <ul className="space-y-1">
          {versions.map((v) => (
            <li key={v.id} className="flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm">
              <FileText className="w-4 h-4 text-muted-foreground shrink-0" />
              <span className="flex-1 text-foreground">Version {v.version}</span>
              <span className="text-xs text-muted-foreground">{timeAgo(v.created_at)}</span>
              <button type="button" onClick={() => download(v.version)}
                className="flex items-center gap-1 text-xs text-foreground underline-offset-2 hover:underline">
                <Download className="w-3 h-3" /> .docx
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}
